/* ============================================================================
   Curated First From Source — HomeRun Prints build for Dawn 15.4.0
   Based on Den Liu's "Curated Collection JS v2025-08-02-b" (WeScale).

   URL contract (unchanged from the original):
     /collections/<any>?first=<handle | /products/handle | https://shop/products/handle?variant=ID>
                       &source=<collection-handle>   (optional, fills the rest of the grid)
                       &limit=<n>                    (optional, trims the grid to n cards)
                       &variant=<id>                 (optional, shows that colour on the pinned card)

   What changed vs. the original and why:
   - The server-rendered grid is kept. Only the pinned card is fetched and
     inserted at slot #1 (or moved there if it is already on the page).
     Fixes the "lonely product" flash and the 1 + N fetch delay reported in
     the comments, and keeps Dawn's own pagination/facets intact.
   - `source=` uses Shopify's Section Rendering API (one request, real Dawn
     cards with your grid settings) with /collections/<source>/products.json
     as a fallback. No collection.handles.json.liquid template needed.
   - Card aspect ratio is read from data-aspect on the grid (added in
     main-collection-product-grid.liquid) so the pinned card matches.
   - Variant image swap builds a proper Shopify CDN srcset instead of the
     old "_533x." filename regex, which Dawn 15 image URLs do not use.
   - No sessionStorage / force-cache (source of the stale-card reports).
============================================================================ */
(() => {
  'use strict';

  const qp = new URLSearchParams(location.search);

  /* Only act on page 1. Dawn's pagination links keep query params, so
     without this the product would be pinned on every page. */
  if ((parseInt(qp.get('page') || '1', 10) || 1) > 1) return;

  /* ---------- 0. Parse `first` (handle | path | full product URL) ---------- */
  let rawFirst = (qp.get('first') || '').trim();
  if (!rawFirst) return;
  try { if (/%[0-9a-f]{2}/i.test(rawFirst)) rawFirst = decodeURIComponent(rawFirst); } catch (_) {}

  let firstHandle = '';
  let variantFromFirst = '';
  if (/^https?:\/\//i.test(rawFirst)) {
    try {
      const u = new URL(rawFirst);
      const segs = u.pathname.split('/').filter(Boolean);
      const i = segs.indexOf('products');
      if (i >= 0 && segs[i + 1]) firstHandle = segs[i + 1];
      variantFromFirst = u.searchParams.get('variant') || '';
    } catch (_) {}
  } else {
    const [path, query = ''] = rawFirst.split('?');
    const segs = path.split('/').filter(Boolean);
    const i = segs.indexOf('products');
    firstHandle = i >= 0 && segs[i + 1] ? segs[i + 1] : (segs.pop() || '');
    variantFromFirst = new URLSearchParams(query).get('variant') || '';
  }
  firstHandle = firstHandle.toLowerCase();
  if (!firstHandle) return;

  const rawVariant = (qp.get('variant') || variantFromFirst || '').trim();
  const variantID = /^\d+$/.test(rawVariant) ? rawVariant : '';

  const pathSegs = location.pathname.split('/').filter(Boolean);
  const currentCollection = pathSegs[0] === 'collections' && pathSegs[1] ? pathSegs[1] : '';
  const source = (qp.get('source') || '').trim();
  const limit = parseInt(qp.get('limit') || '0', 10) || 0;

  /* ---------- 1. Grid + card template selection ---------- */
  const grid = document.querySelector('ul#product-grid');
  if (!grid) return;

  const aspect = (grid.getAttribute('data-aspect') || '').toLowerCase();
  const view = aspect === 'square' ? 'card-square' : aspect === 'portrait' ? 'card-portrait' : 'card';

  const previewExtras = ['preview_theme_id', 'preview_theme_token', 'pb']
    .filter(k => qp.has(k))
    .map(k => `${k}=${encodeURIComponent(qp.get(k) || '')}`)
    .join('&');
  const withPreview = url => previewExtras ? url + (url.includes('?') ? '&' : '?') + previewExtras : url;

  const handleOf = li => {
    const a = li.querySelector('a[href*="/products/"]');
    if (!a) return '';
    const m = a.getAttribute('href').match(/\/products\/([^/?#]+)/i);
    return m ? m[1].toLowerCase() : '';
  };

  const fetchText = async url => {
    const r = await fetch(withPreview(url), { credentials: 'same-origin' });
    return r.ok ? r.text() : '';
  };

  const liFromHTML = html => {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    const li = tpl.content.querySelector('li.grid__item');
    /* card-product.liquid shows Loox stars only when page_type == 'product';
       the card template renders under a product URL, so strip them to match
       the other collection cards. */
    if (li) li.querySelectorAll('.loox-rating').forEach(el => el.remove());
    return li;
  };

  /* ---------- 2. Fill the rest of the grid from `source` (optional) ---------- */
  const fillFromSource = async () => {
    if (!source || source === currentCollection) return;

    /* Preferred: Section Rendering API — one request, real Dawn cards. */
    const sectionId = grid.getAttribute('data-id');
    if (sectionId) {
      const html = await fetchText(`/collections/${encodeURIComponent(source)}?section_id=${encodeURIComponent(sectionId)}`).catch(() => '');
      if (html) {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const srcGrid = tpl.content.querySelector('ul#product-grid');
        if (srcGrid && srcGrid.children.length) {
          grid.replaceChildren(...srcGrid.children);
          return;
        }
      }
    }

    /* Fallback: native products.json + per-card fetch (Thomas Wendy's approach). */
    try {
      const r = await fetch(`/collections/${encodeURIComponent(source)}/products.json?limit=${Math.max(limit, 24)}`, { credentials: 'same-origin' });
      if (!r.ok) return;
      const data = await r.json();
      const handles = (data.products || []).map(p => p.handle).filter(Boolean);
      const cards = await Promise.all(handles.map(h => fetchText(`/products/${encodeURIComponent(h)}?view=${view}`).catch(() => '')));
      const lis = cards.map(liFromHTML).filter(Boolean);
      if (lis.length) grid.replaceChildren(...lis);
    } catch (_) {}
  };

  /* ---------- 3. Pin the product at slot #1 ---------- */
  const pin = async () => {
    /* Already on the page? Move it. Zero network. */
    const existing = Array.from(grid.children).find(li => handleOf(li) === firstHandle);
    if (existing) {
      existing.style.setProperty('--animation-order', '1');
      grid.prepend(existing);
      return existing;
    }

    /* Otherwise reserve slot #1 and fetch the card via the alternate template. */
    const placeholder = document.createElement('li');
    placeholder.className = 'grid__item curated-first__placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    grid.prepend(placeholder);

    const html = await fetchText(`/products/${encodeURIComponent(firstHandle)}?view=${view}`).catch(() => '');
    const li = liFromHTML(html);
    if (!li) { placeholder.remove(); return null; }

    li.classList.add('curated-first__pinned');
    li.style.setProperty('--animation-order', '1');
    placeholder.replaceWith(li);

    /* Drop any duplicate that the source fill may have introduced. */
    Array.from(grid.children).slice(1).forEach(other => { if (handleOf(other) === firstHandle) other.remove(); });
    return li;
  };

  /* ---------- 4. Variant image + links on the pinned card ---------- */
  const applyVariant = async li => {
    if (!variantID || !li) return;
    try {
      const prod = await fetch(`/products/${encodeURIComponent(firstHandle)}.js`, { credentials: 'same-origin' }).then(r => r.json());
      const v = (prod.variants || []).find(x => String(x.id) === variantID);
      if (!v) return;

      li.querySelectorAll('a[href*="/products/"]').forEach(a => {
        const u = new URL(a.getAttribute('href'), location.origin);
        u.searchParams.set('variant', variantID);
        a.setAttribute('href', u.pathname + u.search);
      });

      const src = v.featured_image && v.featured_image.src;
      if (!src) return;
      const base = src.replace(/^\/\//, 'https://').split('?')[0];
      const widths = [165, 360, 533, 720, 940, 1066];
      const img = li.querySelector('.card__media img');
      if (!img) return;
      img.removeAttribute('data-srcset');
      img.srcset = widths.map(w => `${base}?width=${w} ${w}w`).join(', ');
      img.src = `${base}?width=533`;
      if (v.featured_image.width)  img.width  = v.featured_image.width;
      if (v.featured_image.height) img.height = v.featured_image.height;
      if (v.featured_image.alt)    img.alt    = v.featured_image.alt;
    } catch (_) { /* leave default image */ }
  };

  /* ---------- 5. Trim to `limit` ---------- */
  const applyLimit = () => {
    if (!limit) return;
    Array.from(grid.children).slice(limit).forEach(li => li.remove());
  };

  (async () => {
    await fillFromSource();
    const li = await pin();
    applyLimit();
    await applyVariant(li);
    document.dispatchEvent(new CustomEvent('curated-first:done', { detail: { handle: firstHandle, variant: variantID, pinned: !!li } }));
  })().catch(() => { /* never break the collection page */ });
})();
